import { of } from 'rxjs';
import { emptySelectOption } from '../../../../../components/form/elements/select';
import { useService } from '../../../../../services/services';
import { useCallback, useRef } from 'react';
import { useObservableState } from 'observable-hooks';
import { useHistory, useParams } from 'react-router-dom';
import { map } from 'rxjs/operators';
import StatusEnum from '../enums/event-status';
import AnimalType from '../../../../../enums/animal-type';
import { useTranslation } from 'react-i18next';
import { useQuery } from '../../../../../hooks/use-query';
import { LIVESTOCK_ANIMALS_WEIGHING_CREATE } from '../../../../../router/routes';
import { route } from '../../../../../services/utils';

export function useWeighingState() {
  const { farmId, id } = useParams<{
    farmId: string;
    id: string;
  }>();
  const [params] = useQuery();
  const history = useHistory();

  let allAnimal = useRef(
    ((params['animals'] ? params['animals'] : '').split(',') || []).filter(
      Boolean
    )
  );

  const { t } = useTranslation();
  const tp = useCallback(
    (key: string, params: any) =>
      t(
        'pages.livestock.animals_and_events.events.weighing.'.concat(key),
        params
      ),
    [t]
  );

  const { animalEventService, animalService } = useService();

  function calculateAnimalAge(birthDateStr, otherDateStr) {
    let birthDate = new Date(birthDateStr);
    let otherDate = new Date(otherDateStr);

    let years = otherDate.getFullYear() - birthDate.getFullYear();
    let months = otherDate.getMonth() - birthDate.getMonth();
    let days = otherDate.getDate() - birthDate.getDate();

    if (days < 0) {
      months--;
      days += new Date(
        otherDate.getFullYear(),
        otherDate.getMonth(),
        0
      ).getDate();
    }
    if (months < 0) {
      years--;
      months += 12;
    }

    return tp('age', {
      year: years,
      monthes: months,
      days: days,
    });
  }

  const [weighingState] = useObservableState<any>(() => {
    if (id) {
      return animalEventService.show(farmId, id).pipe(
        map(({ data }: any) => {
          return {
            species: emptySelectOption,
            barn_ldc_nr: emptySelectOption,
            animal_id: emptySelectOption,
            status: String(data.status),
            date: data.date,
            weighingsAndAnimals: data.animals.map((item: any) => ({
              ...item,
              animalId: item.id,
              age_days_of_weighing: calculateAnimalAge(
                item.birth_date,
                data.date
              ),
            })),
            notes: data.notes,
            file: data.file ? [data.file] : null,
          };
        })
      );
    } else {
      return of(defaultState());
    }
  }, null);

  const addRow = (animal: any, getValues?: any, setValue?: any) => {
    const weighings = animalService
      .syncAnimalWeighingData(farmId, animal.id)
      .pipe(map((data) => data));

    if (!!animal.id) {
      weighings.subscribe((respone) => {
        const births_weight = respone.animal_weighing.find(
          (el) => el.svaraKods === 'D'
        );

        const data = {
          ...weighingsAndAnimalsInitialData,
          animalId: animal.id,
          animal_id: animal.animal_id,
          name: animal.name,
          breed_code: animal.breed_code,
          birth_date: animal.birth_date,
          age_days_of_weighing:
            animal.birth_date && getValues('date')
              ? calculateAnimalAge(animal.birth_date, getValues('date'))
              : '',
          births_weight: births_weight?.dzivsvars
            ? String(births_weight.dzivsvars)
            : '',
        };

        const weighingsAndAnimals = getValues('weighingsAndAnimals');

        if (
          weighingsAndAnimals.findIndex((item) => item.animalId === animal.id) <
          0
        ) {
          const weighingsAndAnimalsDescriptions = [
            ...weighingsAndAnimals,
            data,
          ];
          allAnimal.current = weighingsAndAnimalsDescriptions.map((animal) => ({
            animal_event_animal_id: null,
            id: animal.animalId,
          }));

          if (!id) {
            history.push(
              route(
                LIVESTOCK_ANIMALS_WEIGHING_CREATE.concat('?animals=:animals'),
                {
                  farmId,
                  animals: weighingsAndAnimalsDescriptions.map(
                    (animal) => animal.animalId
                  ),
                }
              )
            );
          }
          setValue('weighingsAndAnimals', weighingsAndAnimalsDescriptions);
        }
      });
    }
  };

  return { weighingState, addRow };
}

export function defaultState() {
  return {
    event_type: AnimalType.pregnancy,
    species: emptySelectOption,
    barn_ldc_nr: emptySelectOption,
    animal_id: emptySelectOption,
    status: StatusEnum.planned,
    weighingsAndAnimals: [],
    date: '',
    notes: '',
    file: '',
    animals: [],
  };
}

export const weighingsAndAnimalsInitialData = {
  id: '',
  animal_id: '',
  name: '',
  breed_code: '',
  birth_date: '',
  births_weight: '',

  weight_type: emptySelectOption,
  weight: '',
  breast_circumference: '',
  neck_height: '',
  lower_back_height: '',
  modified_weigth: '',
};
